import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Assessment as AssessmentIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  ExitToApp as LogoutIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  RequestPage as RequestIcon,
  History as HistoryIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import { UserRole } from '../../types/auth.types';
import { logout } from '../../store/slices/authSlice';
import RequestsPage from '../requests/RequestsPage';
import ActionHistory from '../requests/ActionHistory';

const Dashboard: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [activeSection, setActiveSection] = useState<'dashboard' | 'requests' | 'action-history' | 'my-requests' | 'pending-approvals'>('dashboard');

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    navigate('/profile');
    handleClose();
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
    handleClose();
  };

  const renderRoleSpecificContent = () => {
    switch (user?.role) {
      case UserRole.SYSTEM_ADMIN:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    User Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage system users and their roles
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    startIcon={<GroupIcon />}
                    onClick={() => navigate('/admin/users')}
                  >
                    Manage Users
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    System Configuration
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configure system settings
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    startIcon={<SettingsIcon />}
                    onClick={() => navigate('/admin/config')}
                  >
                    Settings
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Company Settings
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage company branding and information
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    startIcon={<BusinessIcon />}
                    onClick={() => navigate('/admin/company')}
                  >
                    Company Settings
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        );

      case UserRole.FINANCE_ADMIN:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Pending Requests
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Review and process purchase requests for payment
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<RequestIcon />}
                    onClick={() => setActiveSection('pending-approvals')}
                  >
                    Pending Approvals
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Financial Reports
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View and generate financial reports
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    startIcon={<AssessmentIcon />}
                    onClick={() => navigate('/admin/finance-reports')}
                  >
                    View Reports
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Action History
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View your approval and action history
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<HistoryIcon />}
                    onClick={() => setActiveSection('action-history')}
                  >
                    View History
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        );

      case UserRole.DEPARTMENT_MANAGER:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    My Requests
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create and manage your own purchase requests
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setActiveSection('my-requests')}
                  >
                    My Requests
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Pending Approvals
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Review and approve employee requests
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<RequestIcon />}
                    onClick={() => setActiveSection('pending-approvals')}
                  >
                    View Approvals
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Department Reports
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View department spending reports
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    startIcon={<AssessmentIcon />}
                    onClick={() => navigate('/admin/reports')}
                  >
                    View Reports
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Action History
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View your approval and action history
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<HistoryIcon />}
                    onClick={() => setActiveSection('action-history')}
                  >
                    View History
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        );

      case UserRole.GENERAL_MANAGER:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    My Requests
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create and manage your own purchase requests
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setActiveSection('my-requests')}
                  >
                    My Requests
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Final Approvals
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Review requests for final approval
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<RequestIcon />}
                    onClick={() => setActiveSection('pending-approvals')}
                  >
                    Pending Approvals
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Organization Reports
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View organization-wide analytics and reports
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<AssessmentIcon />}
                    onClick={() => navigate('/admin/gm-reports')}
                  >
                    View Reports
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Action History
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View your approval and action history
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<HistoryIcon />}
                    onClick={() => setActiveSection('action-history')}
                  >
                    View History
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        );

      default: // Employee
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    My Requests
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create and manage your purchase requests
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<RequestIcon />}
                    onClick={() => setActiveSection('requests')}
                  >
                    My Requests
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    My Reports
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View personal spending analytics and trends
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<AssessmentIcon />}
                    onClick={() => navigate('/employee/reports')}
                  >
                    View Reports
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        );
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Purchase Request Management System
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <AccountCircleIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={handleProfile}>
                <PersonIcon sx={{ mr: 1 }} />
                Profile
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {activeSection === 'dashboard' ? (
          <>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h4" gutterBottom>
                Welcome, {user?.firstName}!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {user?.role === UserRole.EMPLOYEE && "Submit and track your purchase requests"}
                {user?.role === UserRole.DEPARTMENT_MANAGER && "Manage your department's purchase requests"}
                {user?.role === UserRole.SENIOR_MANAGER && "Review high-value purchase requests"}
                {user?.role === UserRole.GENERAL_MANAGER && "Final approval for all purchase requests"}
                {user?.role === UserRole.FINANCE_ADMIN && "Mark approved requests as paid"}
                {user?.role === UserRole.CFO && "Review high-value financial requests"}
                {user?.role === UserRole.SYSTEM_ADMIN && "Manage system settings and users"}
              </Typography>
            </Paper>
            {renderRoleSpecificContent()}
          </>
        ) : activeSection === 'action-history' ? (
          <ActionHistory 
            onViewRequest={(request) => {
              // Navigate to requests section and view the specific request
              setActiveSection('requests');
              // Note: We'll need to pass the request ID to RequestsPage
            }}
            onBack={() => setActiveSection('dashboard')}
          />
        ) : activeSection === 'my-requests' ? (
          <RequestsPage 
            onBackToDashboard={() => setActiveSection('dashboard')} 
            mode="my-requests"
          />
        ) : activeSection === 'pending-approvals' ? (
          <RequestsPage 
            onBackToDashboard={() => setActiveSection('dashboard')} 
            mode="pending-approvals"
          />
        ) : (
          <RequestsPage 
            onBackToDashboard={() => setActiveSection('dashboard')} 
            mode={user?.role === UserRole.DEPARTMENT_MANAGER ? undefined : undefined}
          />
        )}
      </Container>
    </Box>
  );
};

export default Dashboard; 